import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PlusCircle, Lifebuoy } from "@phosphor-icons/react";
import { RightRail } from "@/components/customer/RightRail";
import { ChatComposer } from "@/components/customer/ChatComposer";
import { VoicePanel } from "@/components/customer/VoicePanel";
import { useVoiceConversation, useVoiceEnabled } from "@/hooks/useVoiceConversation";
import { MessageBubble } from "@/components/customer/MessageBubble";
import { InfoButton } from "@/components/customer/Disclaimer";
import { useCustomer } from "@/context/CustomerContext";
import { api, streamChat } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { TicketDialog } from "@/components/customer/tickets/TicketDialog";

const TICKET_FIELDS = ["offer_ticket", "ticket_form", "ticket_ref", "rateable", "customer_feedback"];

const CHASSIS_RE = /\bOTM[A-Z0-9]{3,}\b/i;

export default function Chat() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId, caravan, setCaravan, newChat } = useCustomer();
  const isDesktop = useIsDesktop();
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const scrollRef = useRef(null);
  const startedRef = useRef(false);
  const nearBottomRef = useRef(true);
  const prevSessionRef = useRef(sessionId);

  const onMessagesScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    const el = scrollRef.current;
    if (!el) return;
    if (!force && !nearBottomRef.current) return; // don't hijack if user scrolled up
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  // stick to the latest message on every update (unless the user scrolled up)
  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming, scrollToBottom]);

  // when the mobile keyboard opens/closes the viewport resizes — stay at bottom
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => scrollToBottom(true);
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, [scrollToBottom]);

  const send = useCallback(
    async ({ message, image, voice = false, onAudio }) => {
      if (streaming) return null;

      // auto-detect chassis number typed directly in chat — accepts the full
      // "OTM29125" or just the digits "29125" (backend normalizes & validates)
      let chassis = caravan?.chassis_number || null;
      if (!chassis) {
        const m = message.match(CHASSIS_RE);
        const num = message.match(/\b\d{4,7}\b/);
        const candidate = m ? m[0] : num ? num[0] : null;
        if (candidate) {
          try {
            const { data } = await api.get(`/caravans/${encodeURIComponent(candidate)}`);
            setCaravan(data);
            chassis = data.chassis_number;
          } catch {
            /* not a known chassis, ignore */
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: message, image },
        { id: crypto.randomUUID(), role: "assistant", content: "", sources: [], confidence: null },
      ]);
      setStreaming(true);
      nearBottomRef.current = true;
      scrollToBottom(true);

      let doneEvent = null;
      let streamedText = "";
      await streamChat(
        {
          session_id: sessionId,
          message,
          chassis_number: chassis,
          image_base64: image,
          customer_name: caravan?.customer_name || null,
          voice,
        },
        {
          onAudio,
          onStatus: (st) => {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], status: st };
              return copy;
            });
          },
          onToken: (t) => {
            streamedText += t;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + t };
              return copy;
            });
            scrollToBottom();
          },
          onDone: (ev) => {
            doneEvent = { ...ev, content: streamedText };
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1], sources: ev.sources, confidence: ev.confidence, message_id: ev.message_id,
                offer_ticket: ev.offer_ticket, ticket_form: ev.ticket_form, rateable: ev.rateable,
              };
              return copy;
            });
            setStreaming(false);
            scrollToBottom();
          },
          onError: () => {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                content: copy[copy.length - 1].content || "Sorry, I couldn't reach the server. Please try again.",
              };
              return copy;
            });
            setStreaming(false);
          },
        }
      );
      return doneEvent;
    },
    [streaming, caravan, sessionId, setCaravan, scrollToBottom]
  );

  const voiceEnabled = useVoiceEnabled();
  const greet = useCallback(async () => {
    const { data } = await api.post("/voice/welcome", { session_id: sessionId, customer_name: caravan?.customer_name || null });
    if (data.created) {
      setMessages((prev) => [...prev, { id: data.message_id, role: "assistant", content: data.content, sources: [], via: "voice" }]);
    }
    return data;
  }, [sessionId, caravan]);
  const voice = useVoiceConversation(send, greet);
  const lastStatus = messages[messages.length - 1]?.status;

  // initial load / initial message from Home
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (location.state?.voice) {
      navigate(location.pathname, { replace: true, state: {} });
      voice.start();
      return;
    }
    const initial = location.state?.initialMessage;
    const initialImage = location.state?.initialImage;
    const fresh = location.state?.fresh;
    if (initial) {
      navigate(location.pathname, { replace: true, state: {} });
      send({ message: initial, image: initialImage || null });
      return;
    }
    if (fresh) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    // load existing conversation
    api
      .get(`/conversations/${sessionId}`)
      .then(({ data }) => {
        if (data.messages?.length) {
          setMessages(
            data.messages.map((m, i) => ({
              id: m.id || `hist-${i}`,
              role: m.role,
              content: m.content,
              sources: m.sources || [],
              confidence: m.confidence,
              ...Object.fromEntries(TICKET_FIELDS.map((k) => [k, m[k]])),
            }))
          );
          scrollToBottom();
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchMessage = useCallback((id, patch) => setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m))), []);
  const onTicketLodged = useCallback((msg, ref) => patchMessage(msg.id, { ticket_ref: ref }), [patchMessage]);
  const onRated = useCallback((msg, solved, offer) => {
    patchMessage(msg.id, { customer_feedback: solved == null ? null : solved ? "up" : "down", ...(offer ? { offer_ticket: true } : {}) });
  }, [patchMessage]);

  const handleNewChat = () => {
    newChat();
    setMessages([]);
    startedRef.current = true;
  };

  // Starting a New Chat (from anywhere) swaps the session id — clear the thread.
  // Navigating away and back keeps the same session, so history is left intact
  // and restored by the mount effect above.
  useEffect(() => {
    if (prevSessionRef.current === sessionId) return;
    prevSessionRef.current = sessionId;
    setMessages([]);
    startedRef.current = true;
  }, [sessionId]);

  return (
    <div className="h-full flex">
      <div className="flex-1 min-w-0 flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-900 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border border-neutral-700 overflow-hidden shrink-0">
              <img src={ASSETS.nick} alt="Nick" className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold text-white leading-none">Nick</div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#73bf44] pulse-dot" />
                <span className="text-[0.65rem] text-neutral-500 truncate">
                  {caravan ? [caravan.model, caravan.year].filter(Boolean).join(" ") : "Online"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setTicketOpen(true)}
              data-testid="chat-ticket-button"
              title="Lodge a technical support ticket"
              className="flex items-center gap-2 text-sm text-neutral-300 border border-neutral-700 rounded-full px-3 py-1.5 hover:border-[#73bf44] hover:text-white transition-colors"
            >
              <Lifebuoy size={16} /> <span className="hidden md:inline">Support ticket</span>
            </button>
            <button
              onClick={handleNewChat}
              data-testid="chat-new-button"
              className="flex items-center gap-2 text-sm text-neutral-300 border border-neutral-700 rounded-full px-3 sm:px-4 py-1.5 hover:border-[#73bf44] hover:text-white transition-colors"
            >
              <PlusCircle size={16} /> <span className="hidden sm:inline">New Chat</span><span className="sm:hidden">New</span>
            </button>
            <InfoButton />
          </div>
        </div>

        {/* messages */}
        <div ref={scrollRef} onScroll={onMessagesScroll} className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-6" data-testid="chat-messages">
          <div className="max-w-3xl mx-auto space-y-5">
            {messages.length === 0 && (
              <div className="text-center text-neutral-500 pt-20 max-w-md mx-auto">
                <img src={ASSETS.nick} alt="Nick" className="w-16 h-16 rounded-full mx-auto mb-4 border border-neutral-800" />
                <p className="text-sm text-neutral-300 font-medium">G'day! I'm Nick, your OTM technical expert.</p>
                <p className="text-sm mt-2">
                  Ask me anything about your On The Move caravan. If you pop your chassis number
                  (e.g. <span className="text-[#73bf44]">OTM123456</span>) into the chat, I'll tailor answers to
                  your exact setup — no worries if you don't have it handy.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={m.id} msg={m} isLast={!streaming && i === messages.length - 1} onTicketLodged={onTicketLodged} onRated={onRated} />
            ))}
          </div>
        </div>

        {/* composer */}
        <div className="border-t border-neutral-900 px-3 sm:px-4 py-3 sm:py-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shrink-0">
          {voice.active ? (
            <VoicePanel voice={voice} statusText={lastStatus} />
          ) : (
            <ChatComposer
              onSend={send}
              onVoice={voiceEnabled ? voice.start : undefined}
              disabled={streaming}
              autoFocus={isDesktop}
              onFocus={() => scrollToBottom(true)}
            />
          )}
        </div>
      </div>
      <RightRail />
      <TicketDialog open={ticketOpen} onOpenChange={setTicketOpen} />
    </div>
  );
}
